import React, { useEffect, useState } from 'react';
import { KeyRound, RefreshCw, ShieldAlert } from 'lucide-react';
import { useDispatch } from 'react-redux';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { connectSocket, disconnectSocket } from '@/Redux/Action/socketActions';
import { CommanServices } from '@/services/commanServices';
import { SaaSServices } from '@/services/saasServices';
import { apiErrorMessage, readStoredUser, unwrapApiData } from '@/auth/saasSession';
import { supportGrantIsActive } from './saasAdministrationForm';
import { formatInstitutionDateTime } from '../uitilites/institutionTime';

export default function SupportAccess() {
  const dispatch = useDispatch();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pendingClosure, setPendingClosure] = useState(null);
  const [resolution, setResolution] = useState('');

  const load = async () => {
    setLoading(true);
    const response = await SaaSServices.listMySupportRequests();
    const rows = unwrapApiData(response);
    setLoading(false);
    if (!rows) return toast.error(apiErrorMessage(response, 'Support requests could not be loaded'));
    setRequests(rows);
  };

  useEffect(() => { load(); }, []);

  const startSession = async (requestRow) => {
    setLoading(true);
    const response = await SaaSServices.startSupportSession(
      requestRow.institution_id,
      requestRow.id,
      requestRow.break_glass === true,
    );
    const session = unwrapApiData(response);
    setLoading(false);
    if (!session?.access_token) return toast.error(apiErrorMessage(response, 'Support access could not be started'));

    const currentUser = readStoredUser() || {};
    localStorage.setItem('platform_session_backup', JSON.stringify({
      access_token: localStorage.getItem('access_token'),
      user: currentUser,
      active_institution: localStorage.getItem('active_institution'),
      return_path: '/support-access',
    }));
    localStorage.setItem('access_token', session.access_token);
    localStorage.setItem('user', JSON.stringify(session.user));
    localStorage.setItem('active_institution', JSON.stringify({ id: session.institution_id, display_name: requestRow.institution_name || 'Institution', timezone: session.institution_timezone || requestRow.institution_timezone || 'UTC' }));
    localStorage.setItem('support_session', JSON.stringify({
      institution_id: session.institution_id,
      institution_name: requestRow.institution_name || 'Institution',
      grant_id: session.grant_id,
      expires_at: session.expires_at,
      institution_timezone: session.institution_timezone || requestRow.institution_timezone || 'UTC',
      read_only: session.read_only,
      break_glass: session.break_glass,
    }));
    CommanServices.setToken(session.access_token);
    dispatch(disconnectSocket());
    dispatch(connectSocket(session.access_token));
    window.location.assign('/dashboard');
  };

  const closeCase = async () => {
    const requestRow = pendingClosure;
    const summary = resolution.trim();
    if (!requestRow || summary.length < 10) return;
    setLoading(true);
    const response = await SaaSServices.closeSupportCase(requestRow.id, summary);
    const closed = unwrapApiData(response);
    setLoading(false);
    if (!closed) return toast.error(apiErrorMessage(response, 'Support case could not be closed'));
    setRequests((rows) => rows.map((row) => row.id === closed.id ? closed : row));
    setPendingClosure(null);
    setResolution('');
    toast.success(`Support case ${closed.ticket_number || ''} resolved`);
  };

  return (
    <main className="flex h-full overflow-y-auto bg-slate-100 px-5 py-10">
      <section className="m-auto w-full max-w-xl overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl">
        <header className="bg-gradient-to-r from-slate-950 to-blue-950 px-6 py-6 text-white"><div className="flex items-start justify-between gap-4"><div><span className="inline-flex rounded-xl bg-white/10 p-3"><KeyRound className="h-6 w-6" /></span><h1 className="mt-4 text-2xl font-bold">Support requests</h1><p className="mt-2 text-sm leading-6 text-slate-300">Open an assigned request directly. No institution codes or grant identifiers are needed.</p></div><Button type="button" size="sm" variant="outline" className="border-white/30 bg-white/10 text-white hover:bg-white/20" onClick={load} disabled={loading}><RefreshCw className="h-4 w-4" /></Button></div></header>
        <div className="space-y-3 p-6">
          {requests.map((requestRow) => {
            const active = supportGrantIsActive(requestRow);
            const closed = Boolean(requestRow.closed_at);
            const inactiveLabel = closed
              ? 'Resolved'
              : requestRow.revoked_at
              ? 'Revoked'
              : new Date(requestRow.expires_at).getTime() <= Date.now()
                ? 'Expired'
                : 'Unavailable';
            return <article key={requestRow.id} className={`rounded-xl border p-4 ${requestRow.break_glass ? 'border-red-200 bg-red-50' : 'border-slate-200'}`}>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between"><div><div className="flex flex-wrap items-center gap-2"><p className="font-bold text-slate-900">{requestRow.institution_name}</p>{requestRow.break_glass && <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs font-bold text-red-700"><ShieldAlert className="h-3 w-3" /> Emergency</span>}{closed && <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-bold text-emerald-700">Resolved</span>}</div><p className="mt-1 text-sm font-medium text-slate-700">{requestRow.ticket_number}</p><p className="mt-1 text-sm text-slate-500">{requestRow.reason}</p><p className="mt-1 text-xs text-slate-400">Access ends {formatInstitutionDateTime(requestRow.expires_at, requestRow.institution_timezone)}</p>{closed && <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2"><p className="text-xs font-bold uppercase tracking-wide text-emerald-800">Resolution</p><p className="mt-1 text-sm text-emerald-950">{requestRow.resolution || requestRow.resolution_summary}</p><p className="mt-1 text-xs text-emerald-700">Closed {formatInstitutionDateTime(requestRow.closed_at, requestRow.institution_timezone)}{requestRow.closed_by_name ? ` by ${requestRow.closed_by_name}` : ''}</p></div>}</div><div className="flex min-w-[190px] flex-col gap-2"><Button type="button" className={requestRow.break_glass ? 'bg-red-700 hover:bg-red-800' : ''} disabled={loading || !active} onClick={() => startSession(requestRow)}>{active ? (requestRow.break_glass ? 'Start emergency session' : 'Open read-only support') : inactiveLabel}</Button>{!closed && <Button type="button" variant="outline" disabled={loading} onClick={() => { setResolution(''); setPendingClosure(requestRow); }}>Close support case</Button>}</div></div>
            </article>;
          })}
          {!loading && !requests.length && <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center"><KeyRound className="mx-auto h-8 w-8 text-slate-400" /><p className="mt-3 font-semibold text-slate-700">No support requests assigned</p><p className="mt-1 text-sm text-slate-500">New assignments will appear here automatically.</p></div>}
          {loading && !requests.length && <p className="py-8 text-center text-sm text-slate-500">Loading support requests…</p>}
        </div>
      </section>
      <Dialog open={Boolean(pendingClosure)} onOpenChange={(open) => { if (!open && !loading) { setPendingClosure(null); setResolution(''); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Close support case?</DialogTitle>
            <DialogDescription>Record the outcome for {pendingClosure?.ticket_number || 'this request'}. Closing immediately ends any active support session.</DialogDescription>
          </DialogHeader>
          <label htmlFor="support-handler-resolution" className="text-sm font-semibold text-slate-700">
            Resolution summary
            <textarea id="support-handler-resolution" className="mt-1 min-h-28 w-full rounded-md border border-slate-300 px-3 py-2 text-sm" required minLength={10} maxLength={2000} placeholder="Explain what was checked, what was fixed and the final outcome" value={resolution} onChange={(event) => setResolution(event.target.value)} />
            <span className="mt-1 block text-xs font-normal text-slate-500">The institution will see this summary in its support history.</span>
          </label>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => { setPendingClosure(null); setResolution(''); }} disabled={loading}>Keep case open</Button>
            <Button type="button" onClick={closeCase} disabled={loading || resolution.trim().length < 10}>{loading ? 'Closing…' : 'Close as resolved'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </main>
  );
}
